import { ActivityType } from "./ActivityFeed";

/**
 * Centralized TypeScript definitions for analytics events
 * used throughout the Shop Hero Dashboard application.
 *
 * All analytics events follow a consistent structure with:
 * - shopId: string - Identifies the shop context
 * - userId: string - Identifies the user performing the action
 * - timestamp: number - Unix timestamp of when the event occurred
 * - Additional event-specific parameters
 */

// ============================================================================
// Activity Feed Analytics Events
// ============================================================================

/**
 * Event fired when a user applies filters to the Activity Feed.
 * Tracks filter usage patterns and combinations to understand
 * how users navigate and search through activity data.
 */
export interface ActivityFeedFilterAppliedEvent {
  /** Unique identifier for the shop */
  shopId: string;
  /** Unique identifier for the user performing the action */
  userId: string;
  /** Type of filter being applied */
  filterType: "activityType" | "cardNumber" | "dateRange" | "combination";
  /** Selected activity type filter, if applicable */
  activityType?: ActivityType | "all";
  /** Card number being filtered for, if applicable */
  cardNumber?: string;
  /** Whether a date range filter is active */
  hasDateRange: boolean;
  /** Start date for date range filter (ISO string) */
  startDate?: string;
  /** End date for date range filter (ISO string) */
  endDate?: string;
  /** Unix timestamp when the event occurred */
  timestamp: number;
}

/**
 * Event fired when a user loads more items in the Activity Feed pagination.
 * Tracks user engagement depth and pagination performance to optimize
 * loading strategies and understand content consumption patterns.
 */
export interface ActivityFeedLoadMoreEvent {
  /** Unique identifier for the shop */
  shopId: string;
  /** Unique identifier for the user performing the action */
  userId: string;
  /** Current page number being loaded */
  currentPage: number;
  /** Total number of items loaded so far */
  totalItemsLoaded: number;
  /** Number of items per page */
  pageSize: number;
  /** Whether any filters are currently active */
  hasActiveFilters: boolean;
  /** Time taken to load the page in milliseconds */
  loadTimeMs?: number;
  /** Unix timestamp when the event occurred */
  timestamp: number;
}

// ============================================================================
// Inbox Panel Analytics Events
// ============================================================================

/**
 * Source that triggered the inbox panel toggle action.
 * Used to understand user navigation patterns and preferred access methods.
 */
export type InboxPanelTriggerSource =
  | "header_button"
  | "keyboard_shortcut"
  | "auto"
  | "link"
  | "notification";

/**
 * Available tabs within the Inbox Panel.
 * Represents the different sections users can navigate between.
 */
export type InboxPanelTab =
  | "activityFeed"
  | "messageBoard"
  | "goList"
  | "customerUpdates"
  | "dispatch"
  | "tasks"
  | "searchResults";

/**
 * Method used to navigate between tabs.
 * Tracks user interaction preferences for UX optimization.
 */
export type InboxPanelNavigationMethod =
  | "click"
  | "keyboard"
  | "touch"
  | "drag";

/**
 * Event fired when a user opens or closes the Inbox Panel.
 * Tracks panel usage patterns and engagement to optimize
 * panel behavior and understand user workflow preferences.
 */
export interface InboxPanelToggleEvent {
  /** Unique identifier for the shop */
  shopId: string;
  /** Unique identifier for the user performing the action */
  userId: string;
  /** Whether the panel was opened or closed */
  action: "opened" | "closed";
  /** Source that triggered the toggle action */
  triggerSource: InboxPanelTriggerSource;
  /** Current width of the panel in pixels */
  panelWidth: number;
  /** Whether this is the user's first time opening the panel */
  isFirstTimeOpen: boolean;
  /** Duration the panel was open before closing (only for 'closed' action) */
  sessionDurationMs?: number;
  /** Unix timestamp when the event occurred */
  timestamp: number;
}

/**
 * Event fired when a user navigates between tabs within the Inbox Panel.
 * Tracks tab navigation patterns and time spent in each section
 * to understand user preferences and optimize tab organization.
 */
export interface InboxPanelTabNavigationEvent {
  /** Unique identifier for the shop */
  shopId: string;
  /** Unique identifier for the user performing the action */
  userId: string;
  /** Tab the user was previously viewing */
  fromTab: InboxPanelTab;
  /** Tab the user navigated to */
  toTab: InboxPanelTab;
  /** Time spent in the previous tab in milliseconds */
  timeInPreviousTabMs: number;
  /** Method used to navigate between tabs */
  navigationMethod: InboxPanelNavigationMethod;
  /** Unix timestamp when the event occurred */
  timestamp: number;
}

// ============================================================================
// Server-Side Card Movement Analytics Events (WEBV3-30)
// ============================================================================

/** How a card's container change was made. */
export type CardMovementSource = "manual" | "integration" | "automation";

/** Fields common to every server-side card move event. */
export interface CardMovedServerEventBase {
  shopId: string;
  cardId: string;
  cardNumber: string | null;
  fromContainerId: string;
  toContainerId: string;
  movementSource: CardMovementSource;
  /** Seconds spent in the container being exited, or null if unavailable. */
  timeInPreviousContainerSeconds: number | null;
  /** Time from move detection to Mixpanel dispatch, in milliseconds. */
  processingTimeMs: number;
  /** Mixpanel distinct_id: the acting user's uid for manual moves, else a synthetic id. */
  distinct_id: string;
}

/** Fired when a person moves a card between containers. */
export interface CardMovedManualEvent extends CardMovedServerEventBase {
  movementSource: "manual";
}

/** Fired when an integration sync (e.g. Tekmetric) moves a card. */
export interface CardMovedIntegrationEvent extends CardMovedServerEventBase {
  movementSource: "integration";
  /** The integration that wrote the move, e.g. "tekmetric". */
  integrationSource: string;
}

/**
 * Fired when an automation action moves a card. Does not identify which
 * automation caused it in this release — see the WEBV3-30 design spec,
 * "Deferred: automation attribution follow-up ticket".
 */
export interface CardMovedAutomationEvent extends CardMovedServerEventBase {
  movementSource: "automation";
}

// ============================================================================
// Analytics Event Constants
// ============================================================================

/**
 * Standardized event names for analytics tracking.
 * Using constants prevents typos and ensures consistency across the application.
 *
 * Event naming convention follows: "[Feature] [Action]" format
 * e.g., "Activity Feed Filter Applied", "Inbox Panel Toggle"
 */
export const ANALYTICS_EVENTS = {
  /** Fired when user applies filters to Activity Feed */
  ACTIVITY_FEED_FILTER_APPLIED: "Activity Feed Filter Applied",
  /** Fired when user loads more items in Activity Feed */
  ACTIVITY_FEED_LOAD_MORE: "Activity Feed Load More",
  /** Fired when user opens or closes the Inbox Panel */
  INBOX_PANEL_TOGGLE: "Inbox Panel Toggle",
  /** Fired when user navigates between Inbox Panel tabs */
  INBOX_PANEL_TAB_NAVIGATION: "Inbox Panel Tab Navigation",
  /** Fired server-side when a person moves a card between containers */
  CARD_MOVED_MANUAL: "Card Moved - Manual",
  /** Fired server-side when an integration sync moves a card */
  CARD_MOVED_INTEGRATION: "Card Moved - Integration",
  /** Fired server-side when an automation action moves a card */
  CARD_MOVED_AUTOMATION: "Card Moved - Automation",
} as const;

/**
 * Type representing all possible analytics event names.
 * Provides type safety when referencing event names.
 */
export type AnalyticsEventName =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

// ============================================================================
// Union Types and Utilities
// ============================================================================

/**
 * Union type representing all possible analytics event payloads.
 * Useful for generic analytics handling and type guards.
 */
export type AnalyticsEvent =
  | ActivityFeedFilterAppliedEvent
  | ActivityFeedLoadMoreEvent
  | InboxPanelToggleEvent
  | InboxPanelTabNavigationEvent
  | CardMovedManualEvent
  | CardMovedIntegrationEvent
  | CardMovedAutomationEvent;

/**
 * Base interface that all analytics events should extend.
 * Ensures consistent event structure across the application.
 */
export interface BaseAnalyticsEvent {
  /** Unique identifier for the shop */
  shopId: string;
  /** Unique identifier for the user performing the action */
  userId: string;
  /** Unix timestamp when the event occurred */
  timestamp: number;
}

/**
 * Type guard to check if an event is an Activity Feed related event.
 * @param event - The analytics event to check
 * @returns True if the event is Activity Feed related
 */
export const isActivityFeedEvent = (
  event: AnalyticsEvent
): event is ActivityFeedFilterAppliedEvent | ActivityFeedLoadMoreEvent => {
  return "currentPage" in event || "filterType" in event;
};

/**
 * Type guard to check if an event is an Inbox Panel related event.
 * @param event - The analytics event to check
 * @returns True if the event is Inbox Panel related
 */
export const isInboxPanelEvent = (
  event: AnalyticsEvent
): event is InboxPanelToggleEvent | InboxPanelTabNavigationEvent => {
  return "action" in event || "fromTab" in event;
};
